/**
 * BotSpot Web Dashboard Authentication Context
 *
 * Provides authentication state and methods throughout the application.
 */

import React, { createContext, useState, useEffect, useCallback } from "react";
import * as authApi from "../api/auth";
import { getErrorMessage } from "../api/client";
import type { User, LoginRequest, RegisterRequest } from "../api/types";

/**
 * Authentication Context State
 */
interface AuthContextState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  register: (userData: RegisterRequest) => Promise<void>;
  clearError: () => void;
}

/**
 * Authentication Context
 */
export const AuthContext = createContext<AuthContextState | undefined>(undefined);

/**
 * Authentication Provider Props
 */
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * Authentication Provider Component
 *
 * Manages authentication state and provides auth methods to child components.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Initialize authentication state on mount
   */
  useEffect(() => {
    async function initializeAuth() {
      // Try oauth2-proxy authentication first (GitHub OAuth via nginx)
      const proxyResult = await authApi.proxyLogin();
      if (proxyResult) {
        try {
          const currentUser = await authApi.getCurrentUser();
          setUser(currentUser);
          setIsLoading(false);
          return;
        } catch {
          authApi.logout();
        }
      }

      // Fall back to existing JWT in localStorage
      if (authApi.isAuthenticated()) {
        try {
          const currentUser = await authApi.getCurrentUser();
          setUser(currentUser);
        } catch {
          authApi.logout();
          setUser(null);
        }
      }
      setIsLoading(false);
    }

    initializeAuth();
  }, []);

  /**
   * Handle unauthorized events from API client
   */
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      setError("Session expired. Please login again.");
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
    };
  }, []);

  /**
   * Login with username and password
   */
  const login = useCallback(async (credentials: LoginRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      await authApi.login(credentials);
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser);
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout the current user
   */
  const logout = useCallback(() => {
    authApi.logout();
    setUser(null);
    setError(null);
    // Clear the oauth2-proxy cookie by redirecting to its sign-out endpoint
    window.location.href =
      "https://auth.gridheimr.com/oauth2/sign_out?rd=https://app.gridheimr.com";
  }, []);

  /**
   * Register a new user account
   */
  const register = useCallback(async (userData: RegisterRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      await authApi.register(userData);
      // After registration, automatically log in
      await login({
        username: userData.username,
        password: userData.password,
      });
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [login]);

  /**
   * Clear the current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextState = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    error,
    login,
    logout,
    register,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
